<template>
  <div class="settings-page w-full !px-3">
    <div class="courses-section w-full">
      <div class="flex gap-6 w-full">
        <div class="w-full">
          <div class="text-lg font-medium mb-6">Настройка курсов</div>

          <div class="mb-[24px]">
            <div v-if="isLoading" class="text-center py-4">Загрузка...</div>
            <div v-else-if="courses.length === 0" class="text-center py-4">
              Курсов пока нет. Создайте первый курс.
            </div>
            <div v-else class="courses-list">
              <div
                  v-for="course in courses"
                  :key="course.id"
                  class="course-card"
                  @click="onOpenEditCourseModal(course.id)"
              >
                <div class="course-content">
                  <div class="mb-2">
                    <div
                      class="course-tag"
                      :class="[ course.isActive ? '!bg-green-100 !text-green-800' : '!bg-red-100 !text-red-800' ]"
                    >
                      {{ course.isActive ? 'Активен': 'Отключен' }}
                    </div>
                  </div>

                  <div class="course-title">{{ course.title }}</div>
                  <div class="flex gap-2 mb-3">
                    <div class="course-tag">{{ course.lessonsCount }} уроков</div>
                    <div class="course-tag">{{ course.testsCount }} тестов</div>
                  </div>
                  <p class="course-description">{{ course.description || 'Нет описания' }}</p>
                </div>

                <div class="course-arrow">
                  <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <button
              class="h-[40px] w-full px-3 rounded-[6px] bg-[#574AEA] text-white cursor-pointer hover:bg-[#473add]"
              @click="onOpenCourseModal"
          >
            Создать курс
          </button>
        </div>

        <div>
          <SettingsPortal />
        </div>
      </div>
    </div>

    <UModal v-model="isCourse" :fullscreen="true">
      <CreateAndEditCourseModal :courseId="courseId" @onCloseCourseModal="onCloseCourseModal" />
    </UModal>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import apiService from '@/services/api'
import { useToast } from "vue-toastification";

import CreateAndEditCourseModal from "@/components/course/CreateAndEditCourseModal.vue";
import SettingsPortal from "@/components/settings/SettingsPortal.vue"
import UModal from "@/components/ui/UModal.vue";

const toast = useToast();

const isCourse = ref(false)
const courseId = ref(0)
const courses = ref([])
const isLoading = ref(false)

const fetchCourses = async () => {
  isLoading.value = true
  try {
    const response = await apiService.getCourses()
    courses.value = response.data || []
  } catch (error) {
    console.error('Ошибка при загрузке курсов:', error)
  } finally {
    isLoading.value = false
  }
}

const onOpenCourseModal = async () => {
  const data = await apiService.createCourse({
    "title": "Название курса",
    "description": "",
    "extendedDescription": ``,
    "accessibleUsers": [],
    "isActive": true,
  })

  if (!data) {
    toast.error('Не удалось создать курс. Напишите в тех. поддержку')
  }

  console.log('data', data)

  courseId.value = data.id
  isCourse.value = true
}

const onOpenEditCourseModal = (id) => {
  courseId.value = id
  isCourse.value = true
}

const onCloseCourseModal = () => {
  courseId.value = 0
  isCourse.value = false
  fetchCourses() // Обновляем список курсов после закрытия модального окна
}

onMounted(() => {
  fetchCourses()
})
</script>

<style scoped>
.settings-page {
  padding: 24px 0;
}

.courses-section {
  margin-bottom: 32px;
}

.courses-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.course-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px -8px rgba(0, 0, 0, 0.15);
  border-color: transparent;
}

.course-content {
  flex: 1;
}

.course-tag {
  display: inline-block;
  padding: 4px 10px;
  background-color: #e8e5ff;
  color: #574AEA;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.course-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
  line-height: 1.3;
}

.course-description {
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
}

.course-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.course-card:hover .course-arrow {
  background: #574AEA;
}

.course-card:hover .course-arrow svg {
  color: white;
}

h2 {
  margin-bottom: 16px;
  color: #333;
}

.create-course-button {
  background: #42b983;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.create-course-button:hover {
  background: #38a169;
}

.course-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

/* Мобильная адаптивность */
@media (max-width: 768px) {
  .settings-page {
    padding: 12px;
    width: 100%;
  }
  
  /* Основной контейнер */
  .courses-section {
    margin-bottom: 20px;
    width: 100%;
  }
  
  .w-full {
    width: 100%;
  }
  
  /* Layout адаптация */
  .flex.gap-6 {
    flex-direction: column;
    gap: 20px;
  }
  
  /* Заголовок */
  .text-lg.font-medium.mb-6 {
    font-size: 1.125rem;
    margin-bottom: 16px;
    padding: 0 4px;
  }
  
  /* Сетка курсов */
  .courses-list {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 20px;
  }
  
  /* Карточки курсов */
  .course-card {
    padding: 16px;
    border-radius: 16px;
    gap: 12px;
    margin: 0 4px;
  }
  
  .course-content {
    flex: 1;
  }
  
  .flex.gap-2.mb-3 {
    gap: 8px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  
  .course-tag {
    padding: 4px 8px;
    font-size: 0.7rem;
    border-radius: 8px;
  }
  
  .course-title {
    font-size: 1rem;
    margin-bottom: 4px;
  }
  
  .course-description {
    font-size: 0.8rem;
    line-height: 1.4;
  }
  
  .course-arrow {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }
  
  .w-5.h-5 {
    width: 16px;
    height: 16px;
  }
  
  /* Кнопка создания курса */
  .h-\[40px\].w-full.px-3.rounded-\[6px\] {
    height: 48px;
    padding: 0 16px;
    border-radius: 12px;
    font-size: 0.875rem;
    margin: 0 4px;
    font-weight: 600;
  }
  
  /* Загрузка и пустые состояния */
  .text-center.py-4 {
    padding: 20px 16px;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .settings-page {
    padding: 8px;
  }
  
  .text-lg.font-medium.mb-6 {
    font-size: 1rem;
    margin-bottom: 12px;
    padding: 0 2px;
  }
  
  .courses-list {
    gap: 12px;
    margin-bottom: 16px;
  }
  
  .course-card {
    padding: 12px;
    border-radius: 12px;
    gap: 8px;
    margin: 0 2px;
  }
  
  .flex.gap-2.mb-3 {
    gap: 6px;
    margin-bottom: 8px;
  }
  
  .course-tag {
    padding: 3px 6px;
    font-size: 0.65rem;
  }
  
  .course-title {
    font-size: 0.9rem;
    margin-bottom: 3px;
  }
  
  .course-description {
    font-size: 0.75rem;
  }
  
  .course-arrow {
    width: 24px;
    height: 24px;
  }
  
  .w-5.h-5 {
    width: 14px;
    height: 14px;
  }
  
  .h-\[40px\].w-full.px-3.rounded-\[6px\] {
    height: 44px;
    padding: 0 12px;
    border-radius: 10px;
    font-size: 0.8rem;
    margin: 0 2px;
  }
  
  .text-center.py-4 {
    padding: 16px 12px;
    font-size: 0.8rem;
  }
}
</style>
